import { useParams, Link } from "react-router-dom";
import {
  GraduationCap, BookOpen, Briefcase, Building2, FileText,
  Award, Users, CheckCircle, ArrowLeft, Loader2, AlertCircle,
  Clock, Star, ScrollText, Heart, Fingerprint, Network, User,
} from "lucide-react";
import {
  useGetUpgradeApplicationDetailsQuery,
  useGetFellowshipApplicationDetailsQuery,
} from "../api/upgradeApi";
import { useGetFirmApplicationDetailsQuery } from "../../firm-application/api/firmApi";

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function StatusBadge({ status }: { status?: string }) {
  const s = (status ?? "pending").toLowerCase();
  const map: Record<string, string> = {
    approved:     "bg-green-100 text-green-700",
    rejected:     "bg-red-100 text-red-700",
    under_review: "bg-blue-100 text-blue-700",
    submitted:    "bg-indigo-100 text-indigo-700",
    pending:      "bg-amber-100 text-amber-700",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold uppercase ${map[s] ?? map.pending}`}>
      {status ?? "Pending"}
    </span>
  );
}

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
      <div className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2.5">
        {icon}
        <span className="text-sm font-semibold">{title}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Field({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-gray-400 font-semibold uppercase tracking-wide">{label}</span>
      <span className="text-sm text-gray-800">
        {value ?? <span className="text-gray-400 italic">Not provided</span>}
      </span>
    </div>
  );
}

function Table({ heads, rows }: { heads: string[]; rows: (string | number | undefined | null)[][] }) {
  if (!rows || rows.length === 0) return <p className="text-sm text-gray-400 italic">No entries recorded.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            <th className="text-left py-1.5 pr-3 text-xs font-semibold text-gray-500 w-8">SN</th>
            {heads.map(h => <th key={h} className="text-left py-1.5 pr-3 text-xs font-semibold text-gray-500">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b last:border-0 hover:bg-gray-50">
              <td className="py-2 pr-3 text-gray-400">{i + 1}</td>
              {row.map((cell, j) => <td key={j} className="py-2 pr-3 text-gray-700">{cell ?? "—"}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function refName(r?: any) {
  if (!r) return "—";
  return [r.surname, r.other_names ?? r.firstname].filter(Boolean).join(" ") || r.name || "—";
}

// ── Membership detail ────────────────────────────────────────────────────────

function MembershipDetail({ app }: { app: any }) {
  const olevel: any[]    = app.olevel_results ?? [];
  const academic: any[]  = app.institutions ?? [];
  const profBodies: any[]= app.professional_bodies ?? [];
  const workExp: any[]   = app.work_experiences ?? [];
  const seminars: any[]  = app.seminars ?? [];
  const documents: any[] = app.documents ?? [];

  return (
    <>
      <Section icon={<BookOpen className="w-4 h-4" />} title="O'Level Results">
        <Table
          heads={["Subject", "Grade", "Year", "Exam Body"]}
          rows={olevel.map((r: any) => [r.subject, r.grade, r.year, r.exam_body])}
        />
      </Section>

      <Section icon={<GraduationCap className="w-4 h-4" />} title="Academic Institutions">
        <Table
          heads={["Institution", "Course", "Graduation Year"]}
          rows={academic.map((r: any) => [r.other_institution || r.institution, r.course, r.graduation_year])}
        />
      </Section>

      <Section icon={<Award className="w-4 h-4" />} title="Professional Bodies">
        <Table
          heads={["Body", "Membership Grade", "Induction Year"]}
          rows={profBodies.map((r: any) => [r.body, r.membership_grade, r.induction_year])}
        />
      </Section>

      <Section icon={<Briefcase className="w-4 h-4" />} title="Work Experience">
        <Table
          heads={["Employer", "Position", "Duties", "Date"]}
          rows={workExp.map((r: any) => [r.employer, r.position, r.duties, r.employment_date])}
        />
      </Section>

      {app.employer_email && (
        <Section icon={<Building2 className="w-4 h-4" />} title="Employer Details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Employer's Email" value={app.employer_email} />
            {app.employer_comment && <Field label="Employer's Comment" value={app.employer_comment} />}
          </div>
        </Section>
      )}

      <Section icon={<GraduationCap className="w-4 h-4" />} title="Professional Development">
        <Table
          heads={["Seminar / Workshop", "Organizer", "CPD Units", "Date"]}
          rows={seminars.map((r: any) => [r.title, r.organizer, r.cpd_units, r.date])}
        />
      </Section>

      <Section icon={<FileText className="w-4 h-4" />} title="Required Documents">
        {documents.length === 0 ? (
          <p className="text-sm text-gray-400 italic">No documents recorded.</p>
        ) : (
          <div className="space-y-2">
            {documents.map((doc: any, i: number) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                <span className="font-medium text-gray-800">{doc.doc_type}</span>
                {doc.file_name && <span className="text-gray-400 text-xs">({doc.file_name})</span>}
              </div>
            ))}
          </div>
        )}
      </Section>
    </>
  );
}

// ── Fellowship detail ────────────────────────────────────────────────────────

function FellowshipDetail({ app }: { app: any }) {
  const academic: any[]    = app.institutions ?? [];
  const profBodies: any[]  = app.professional_bodies ?? [];
  const workExp: any[]     = app.work_experiences ?? [];
  const seminars: any[]    = app.seminars ?? [];
  const papers: any[]      = app.papers ?? [];
  const benevolence: any[] = app.benevolence_records ?? [];
  const documents: any[]   = app.documents ?? [];

  return (
    <>
      <Section icon={<GraduationCap className="w-4 h-4" />} title="Academic Institutions">
        <Table
          heads={["Institution", "Course", "Graduation Year"]}
          rows={academic.map((r: any) => [r.other_institution || r.institution, r.course, r.graduation_year])}
        />
      </Section>

      <Section icon={<Award className="w-4 h-4" />} title="Professional Bodies">
        <Table
          heads={["Body", "Membership Grade", "Induction Year"]}
          rows={profBodies.map((r: any) => [r.body, r.membership_grade, r.induction_year])}
        />
      </Section>

      <Section icon={<Briefcase className="w-4 h-4" />} title="Work Experience">
        <Table
          heads={["Employer", "Position", "Duties", "Date"]}
          rows={workExp.map((r: any) => [r.employer, r.position, r.duties, r.employment_date])}
        />
      </Section>

      <Section icon={<GraduationCap className="w-4 h-4" />} title="Professional Development">
        <Table
          heads={["Seminar / Workshop", "Organizer", "CPD Units", "Date"]}
          rows={seminars.map((r: any) => [r.title, r.organizer, r.cpd_units, r.date])}
        />
      </Section>

      <Section icon={<ScrollText className="w-4 h-4" />} title="Papers Presented">
        <Table
          heads={["Paper Title", "Event Presented", "Year"]}
          rows={papers.map((r: any) => [r.paper_title, r.event_presented, r.year])}
        />
      </Section>

      <Section icon={<Heart className="w-4 h-4" />} title="Evidence of Benevolence">
        <Table
          heads={["Description", "Type", "Year"]}
          rows={benevolence.map((r: any) => [r.description, r.benevolence_type, r.year])}
        />
      </Section>

      <Section icon={<FileText className="w-4 h-4" />} title="Required Documents">
        {documents.length === 0 ? (
          <p className="text-sm text-gray-400 italic">No documents recorded.</p>
        ) : (
          <div className="space-y-2">
            {documents.map((doc: any, i: number) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                <span className="font-medium text-gray-800">{doc.doc_type}</span>
                {doc.file_name && <span className="text-gray-400 text-xs">({doc.file_name})</span>}
              </div>
            ))}
          </div>
        )}
      </Section>
    </>
  );
}

// ── Firm Application detail ──────────────────────────────────────────────────

function FirmDetail({ app }: { app: any }) {
  const documents: any[] = app.documents ?? [];
  const benevolence: any[] = app.benevolence_records ?? [];

  return (
    <>
      <Section icon={<Building2 className="w-4 h-4" />} title="Firm / Practice Details">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Practice Name" value={app.practice_name} />
          <Field label="Nature of Practice" value={app.nature_of_practice} />
          <Field label="Registered Address" value={app.registered_address} />
          <Field label="Postal Address" value={app.postal_address} />
          <Field label="CAMA Reg. Number" value={app.cama_reg_number} />
          <Field label="CAC Registration Date" value={app.cac_reg_date} />
          <Field label="Commencement Date" value={app.commencement_date} />
          <Field label="State" value={app.state} />
          <Field label="Phone" value={app.phone} />
          <Field label="Email" value={app.email} />
        </div>
      </Section>

      <Section icon={<Network className="w-4 h-4" />} title="Ownership Structure & Offices">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <Field label="Ownership Structure" value={app.ownership_structure} />
          <Field label="Number of Partners / Directors" value={app.num_partners} />
        </div>
        {app.branch_offices?.length > 0 && (
          <div className="mb-2">
            <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Branch Offices</p>
            <ul className="text-sm text-gray-700 space-y-0.5">
              {app.branch_offices.map((o: string, i: number) => <li key={i}>• {o}</li>)}
            </ul>
          </div>
        )}
        {app.affiliations?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Affiliations</p>
            <ul className="text-sm text-gray-700 space-y-0.5">
              {app.affiliations.map((a: string, i: number) => <li key={i}>• {a}</li>)}
            </ul>
          </div>
        )}
      </Section>

      <Section icon={<User className="w-4 h-4" />} title="Principal Partner CV">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Full Name" value={app.principal_full_name} />
          <Field label="Maiden Name" value={app.principal_maiden_name} />
          <Field label="Age" value={app.principal_age} />
          <Field label="NIQS Membership No." value={app.principal_niqs_membership_no} />
          <Field label="NIQS Election Date" value={app.principal_niqs_election_date} />
          <Field label="QSRBN Reg. No." value={app.principal_qsrbn_reg_no} />
          <Field label="QSRBN Reg. Date" value={app.principal_qsrbn_reg_date} />
          <div className="md:col-span-2">
            <Field label="Academic Qualifications" value={app.principal_academic_qual} />
          </div>
          <div className="md:col-span-2">
            <Field label="Professional Qualifications" value={app.principal_professional_qual} />
          </div>
          <div className="md:col-span-2">
            <Field label="Post-Qualification Experience" value={app.principal_post_qual_exp} />
          </div>
        </div>
      </Section>

      <Section icon={<Heart className="w-4 h-4" />} title="Acts of Benevolence to NIQS">
        <Table
          heads={["Description", "Year", "Value (₦)"]}
          rows={benevolence.map((r: any) => [r.description, r.year, r.value])}
        />
      </Section>

      <Section icon={<FileText className="w-4 h-4" />} title="Required Documents">
        {documents.length === 0 ? (
          <p className="text-sm text-gray-400 italic">No documents recorded.</p>
        ) : (
          <div className="space-y-2">
            {documents.map((doc: any, i: number) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                <span className="font-medium text-gray-800">{doc.doc_type}</span>
                {doc.file_name && <span className="text-gray-400 text-xs">({doc.file_name})</span>}
              </div>
            ))}
          </div>
        )}
      </Section>
    </>
  );
}

// ── Shared references section ────────────────────────────────────────────────

function ReferencesSection({ app }: { app: any }) {
  return (
    <Section icon={<Users className="w-4 h-4" />} title="State Chapter / References">
      <div className="space-y-4">
        {app.state_chapter && (
          <Field label="State Chapter" value={app.state_chapter} />
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[app.referee1, app.referee2].map((ref: any, i: number) => (
            <div key={i} className="border rounded-xl px-4 py-3 bg-gray-50 space-y-1">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Referee {i + 1}</p>
              {ref ? (
                <>
                  <p className="text-sm font-semibold text-gray-900">{refName(ref)}</p>
                  {ref.membership_no && (
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      <Fingerprint className="w-3 h-3" /> {ref.membership_no}
                    </span>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-400 italic">Not recorded</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

function DetailShell({
  kind,
  id,
  app,
  error,
  isLoading,
}: {
  kind: string;
  id: string;
  app: any;
  error: any;
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center gap-2 py-32 text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin" />
        <p className="text-sm">Loading application...</p>
      </div>
    );
  }

  if (error || !app) {
    return (
      <div className="max-w-xl mx-auto py-20 text-center space-y-4 p-6">
        <AlertCircle className="w-10 h-10 text-red-400 mx-auto" />
        <p className="text-sm text-gray-500">
          {error?.data?.message ?? "Application not found or could not be loaded."}
        </p>
        <Link
          to="/upgrade-applications"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to My Applications
        </Link>
      </div>
    );
  }

  const isFellowship = kind === "fellowship";
  const isFirm = kind === "firm";
  const title = isFirm ? "Firm Application" : isFellowship ? "Fellowship Upgrade" : "Membership Upgrade";

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="bg-white border rounded-xl p-5 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className={`p-2.5 rounded-lg border mt-0.5 ${
              isFirm ? "bg-green-50 border-green-100"
              : isFellowship ? "bg-amber-50 border-amber-100"
              : "bg-indigo-50 border-indigo-100"
            }`}>
              {isFirm
                ? <Building2 className="w-5 h-5 text-green-600" />
                : isFellowship
                ? <Star className="w-5 h-5 text-amber-500" />
                : <Award className="w-5 h-5 text-indigo-600" />
              }
            </div>
            <div>
              <p className="text-xs text-gray-400 font-mono mb-0.5">#{String(id).padStart(6, "0")}</p>
              <h1 className="text-xl font-bold text-gray-900">{title}</h1>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <StatusBadge status={app.status} />
                <span className="flex items-center gap-1 text-xs text-gray-400">
                  <Clock className="w-3 h-3" /> Submitted {fmtDate(app.created_at)}
                </span>
                {app.reviewed_at && (
                  <span className="text-xs text-gray-400">Reviewed {fmtDate(app.reviewed_at)}</span>
                )}
              </div>
              {app.remarks && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  <span className="font-semibold">Admin remark: </span>{app.remarks}
                </div>
              )}
            </div>
          </div>

          <Link
            to="/upgrade-applications"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm hover:bg-gray-50 transition-colors text-gray-600 shrink-0"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        </div>
      </div>

      {/* Sections */}
      {isFirm
        ? <FirmDetail app={app} />
        : isFellowship
        ? <FellowshipDetail app={app} />
        : <MembershipDetail app={app} />
      }

      {isFirm ? (
        <Section icon={<Users className="w-4 h-4" />} title="State Chapter / References">
          <div className="space-y-4">
            {app.state_chapter && <Field label="State Chapter" value={app.state_chapter} />}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[app.referee1, app.referee2].map((ref: any, i: number) => (
                <div key={i} className="border rounded-xl px-4 py-3 bg-gray-50 space-y-1">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Referee {i + 1}</p>
                  {ref ? (
                    <>
                      <p className="text-sm font-semibold text-gray-900">{refName(ref)}</p>
                      {ref.membership_no && (
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                          <Fingerprint className="w-3 h-3" /> {ref.membership_no}
                        </span>
                      )}
                    </>
                  ) : <p className="text-sm text-gray-400 italic">Not recorded</p>}
                </div>
              ))}
            </div>
            {(app.chapter_chairman_name || app.chapter_secretary_name) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                {app.chapter_chairman_name && (
                  <div className="border rounded-xl px-4 py-3 bg-gray-50 space-y-1">
                    <p className="text-xs font-semibold text-gray-400 uppercase">Chapter Chairman</p>
                    <p className="text-sm font-medium text-gray-800">{app.chapter_chairman_name}</p>
                    {app.chapter_chairman_email && <p className="text-xs text-gray-500">{app.chapter_chairman_email}</p>}
                    {app.chapter_chairman_phone && <p className="text-xs text-gray-500">{app.chapter_chairman_phone}</p>}
                  </div>
                )}
                {app.chapter_secretary_name && (
                  <div className="border rounded-xl px-4 py-3 bg-gray-50 space-y-1">
                    <p className="text-xs font-semibold text-gray-400 uppercase">Chapter Secretary</p>
                    <p className="text-sm font-medium text-gray-800">{app.chapter_secretary_name}</p>
                    {app.chapter_secretary_email && <p className="text-xs text-gray-500">{app.chapter_secretary_email}</p>}
                    {app.chapter_secretary_phone && <p className="text-xs text-gray-500">{app.chapter_secretary_phone}</p>}
                  </div>
                )}
              </div>
            )}
          </div>
        </Section>
      ) : (
        <ReferencesSection app={app} />
      )}

      <div className="flex justify-center pb-4">
        <Link
          to="/upgrade-applications"
          className="px-6 py-2.5 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm font-medium transition-colors"
        >
          Back to My Applications
        </Link>
      </div>
    </div>
  );
}

// ── Two thin wrappers that pick the right query ──────────────────────────────

function MembershipDetailLoader({ id }: { id: string }) {
  const { data, isLoading, error } = useGetUpgradeApplicationDetailsQuery(id);
  const app = data?.data ?? data;
  return <DetailShell kind="membership" id={id} app={app} error={error} isLoading={isLoading} />;
}

function FellowshipDetailLoader({ id }: { id: string }) {
  const { data, isLoading, error } = useGetFellowshipApplicationDetailsQuery(id);
  const app = data?.data ?? data;
  return <DetailShell kind="fellowship" id={id} app={app} error={error} isLoading={isLoading} />;
}

function FirmDetailLoader({ id }: { id: string }) {
  const { data, isLoading, error } = useGetFirmApplicationDetailsQuery(id);
  const app = data?.data ?? data;
  return <DetailShell kind="firm" id={id} app={app} error={error} isLoading={isLoading} />;
}

export default function UpgradeApplicationDetailsPage() {
  const { kind, id } = useParams<{ kind: string; id: string }>();

  if (!id) {
    return (
      <div className="py-20 text-center text-sm text-gray-400">Invalid application link.</div>
    );
  }

  if (kind === "fellowship") return <FellowshipDetailLoader id={id} />;
  if (kind === "firm") return <FirmDetailLoader id={id} />;
  return <MembershipDetailLoader id={id} />;
}
